import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";
import { Check } from "react-feather";

import ActionButton from "@/components/atoms/buttons/ActionButton";
import CTAButton from "@/components/atoms/buttons/CTAButton";
import { ITextButton } from "@/components/atoms/buttons/types";
import { IBriefCallout, IProofBullet } from "@/api/dtos/block-component.type";

// ----------------------------------------------------------------------------
// Slot 3 — Bold POV / manifesto + sample deliverable.
//
// Strapi-driven (block: blocks.philosophy-deliverable-section). All content
// flows through props now; nothing is hardcoded except the visual scaffolding
// (callout colors/rotations, mock fallback when no image is uploaded).
//
// See R&D rationale and design history in the original commit / PR #703.
// ----------------------------------------------------------------------------

export type IHowEngagementRunsSectionProps = HTMLAttributes<HTMLElement> & {
  eyebrow?: string;
  headlineMain?: string;
  emphasisWord?: string;
  headlineFollowup?: string;
  body?: string;
  proofBullets?: IProofBullet[];
  primaryCta?: ITextButton;
  secondaryCta?: ITextButton;
  sampleBriefImage?: string;
  sampleBriefImageAlt?: string;
  sampleBriefCaption?: string;
  sampleBriefCallouts?: IBriefCallout[];
  subtitleHighlight?: string;
};

// Callout pill colors + rotations cycle by index — keeps visual feel
// hand-crafted without exposing them as Strapi fields.
const CALLOUT_COLORS = [
  "bg-primary-900 text-white",
  "bg-yellow-400 text-black-950",
  "bg-secondary-900 text-white",
  "bg-red-600 text-white",
] as const;
const CALLOUT_ROTATIONS = ["-rotate-3", "rotate-2", "-rotate-1", "rotate-3"];

const CALLOUT_POSITION_CLASS: Record<IBriefCallout["position"], string> = {
  left_top: "left-[-12%] top-[16%]",
  right_top: "right-[-10%] top-[36%]",
  left_bottom: "left-[-8%] top-[64%]",
  right_bottom: "right-[-12%] bottom-[18%]",
};

// Auto-italicizes text wrapped in smart quotes (curly " ") so a Strapi
// editor can type a quoted question in headline_followup and it renders
// italic without needing rich-text formatting on the field.
const renderWithItalicQuotes = (text: string): ReactNode => {
  // Match smart-quoted spans OR fall back to any straight-quoted span
  const parts = text.split(/(“[^”]*”|"[^"]+")/g);
  return parts.map((part, i) => {
    if (
      (part.startsWith("“") && part.endsWith("”")) ||
      (part.startsWith('"') && part.endsWith('"'))
    ) {
      return (
        <span key={i} className="italic">
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// Reusable gradient utility — primary blue → secondary light-blue
// bg-clip-text. Used on eyebrow, emphasis word, and subtitle highlight.
const GRADIENT_TEXT =
  "bg-gradient-to-r from-primary-900 to-secondary-900 bg-clip-text text-transparent";

// Splits headline_main into [before, emphasis, after] so the emphasis word
// gets BOTH the yellow underline highlight AND the gradient text fill —
// makes it the visual focal point of the manifesto.
const renderHeadlineWithEmphasis = (
  headline: string,
  emphasisWord?: string,
): ReactNode => {
  if (!emphasisWord) return headline;
  const idx = headline.indexOf(emphasisWord);
  if (idx < 0) return headline;
  const before = headline.slice(0, idx);
  const after = headline.slice(idx + emphasisWord.length);
  return (
    <>
      {before}
      <span className="relative inline-block">
        <span className={clsx("relative z-10", GRADIENT_TEXT)}>
          {emphasisWord}
        </span>
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-1 z-0 h-3 -skew-x-6 bg-yellow-300 md:bottom-2 md:h-4 xl:bottom-3 xl:h-5"
        />
      </span>
      {after}
    </>
  );
};

// Optional phrase highlight in the followup line. Strapi-driven via the
// new subtitle_highlight field — if set and found verbatim, the phrase
// gets the same gradient text treatment. Lets a content editor pop one
// phrase per subtitle without rich-text formatting.
const renderFollowupWithHighlight = (
  text: string,
  highlightPhrase?: string,
): ReactNode => {
  // Italic auto-detect first (preserve existing behavior)
  const withItalics = renderWithItalicQuotes(text);

  // If no highlight phrase, return as-is
  if (!highlightPhrase) return withItalics;

  // Re-walk the text to find + wrap the highlight phrase; works even if
  // the phrase spans italic boundaries because we split the ORIGINAL
  // string here (not the already-rendered nodes).
  const idx = text.indexOf(highlightPhrase);
  if (idx < 0) return withItalics;

  const before = text.slice(0, idx);
  const after = text.slice(idx + highlightPhrase.length);
  return (
    <>
      {renderWithItalicQuotes(before)}
      <span className={clsx("font-semibold", GRADIENT_TEXT)}>
        {highlightPhrase}
      </span>
      {renderWithItalicQuotes(after)}
    </>
  );
};

const HowEngagementRunsSection: FC<IHowEngagementRunsSectionProps> = ({
  eyebrow,
  headlineMain,
  emphasisWord,
  headlineFollowup,
  body,
  proofBullets,
  primaryCta,
  secondaryCta,
  sampleBriefImage,
  sampleBriefImageAlt,
  sampleBriefCaption,
  sampleBriefCallouts,
  subtitleHighlight,
  className,
  ...other
}) => {
  if (!headlineMain) return null;

  const callouts = sampleBriefCallouts ?? [];

  return (
    <section
      className={clsx(
        "container mx-auto py-14 md:py-20 xl:py-[100px]",
        className,
      )}
      {...other}
    >
      <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14 xl:gap-20">
        {/* LEFT — manifesto */}
        <div className="lg:col-span-7">
          {eyebrow && (
            <p
              className={clsx(
                "mb-5 font-bricolage-grotesque text-sm font-semibold uppercase tracking-[0.25em] md:text-base",
                GRADIENT_TEXT,
              )}
            >
              {eyebrow}
            </p>
          )}

          {/* Title + followup are rendered as one continuous statement:
              same font, same color, same weight, only ONE size step down
              from title → followup (not the previous 2-step drop that
              made them feel like separate thoughts). Italic auto-applied
              to text inside smart quotes so questions like "what do we
              fix this week?" read as quoted speech. */}
          {/* Matches the canonical SectionHeader title scale used by
              "Building blocks of a solid cybersecurity strategy" below,
              so both section titles read at the same size + weight. */}
          <h2 className="font-bricolage-grotesque text-[clamp(1.375rem,3vw+0.75rem,3rem)] font-medium !leading-[1.1] text-black-900">
            {renderHeadlineWithEmphasis(headlineMain, emphasisWord)}
          </h2>

          {headlineFollowup && (
            // Sits between the canonical title clamp (30–48px) and the
            // description clamp (16–18px). Was previously oversized at
            // 24–36px which made the followup feel like a second title.
            <p className="mt-4 font-bricolage-grotesque text-[clamp(1.125rem,1vw+0.75rem,1.5rem)] font-medium !leading-[1.25] text-black-900 md:mt-5">
              {renderFollowupWithHighlight(headlineFollowup, subtitleHighlight)}
            </p>
          )}

          {body && (
            // Matches the canonical SectionHeader subtitle scale used by
            // "Building blocks…" description below, so both section
            // descriptions read at the same size + color + line-height.
            <p className="mt-6 max-w-xl text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] leading-relaxed text-black-700 md:mt-8">
              {body}
            </p>
          )}

          {proofBullets && proofBullets.length > 0 && (
            <ul className="mt-8 space-y-4 md:space-y-5">
              {proofBullets.map((b) => (
                <li key={b.id ?? b.label} className="flex gap-4">
                  <span
                    aria-hidden="true"
                    className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-900 text-white"
                  >
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <div>
                    <p className="font-bricolage-grotesque text-base font-medium text-black-900 md:text-lg">
                      {b.label}
                    </p>
                    <p className="text-sm leading-relaxed text-black-600 md:text-base">
                      {b.detail}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {primaryCta?.link && primaryCta?.text && (
            <div className="mt-8 md:mt-10">
              <CTAButton
                id={primaryCta.id}
                href={primaryCta.link}
                target={primaryCta.target}
                variant="primary"
                radius="full"
                size="md"
                showIcon
                iconType="arrow"
                iconPosition="right"
              >
                {primaryCta.text}
              </CTAButton>
            </div>
          )}
        </div>

        {/* RIGHT — sample deliverable */}
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black-100">
              {sampleBriefImage ? (
                // Real redacted PDF screenshot uploaded via Strapi.
                <Image
                  src={sampleBriefImage}
                  alt={sampleBriefImageAlt || "Sample board brief"}
                  fill
                  sizes="(max-width: 1024px) 90vw, 40vw"
                  className="object-cover"
                />
              ) : (
                // Fallback CSS mock — shows when no sample_brief_image
                // has been uploaded in Strapi. Same visual rhythm as the
                // real PDF so the design holds during content rollout.
                <>
                  <div className="flex items-center justify-between border-b border-black-100 px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-20 rounded-full bg-primary-900" />
                      <div className="h-2.5 w-12 rounded-full bg-black-200" />
                    </div>
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700">
                      Critical
                    </span>
                  </div>
                  <div className="space-y-3.5 p-5 md:space-y-4 md:p-6">
                    <div className="h-3 w-3/4 rounded bg-black-200" />
                    <div className="h-2 w-full rounded bg-black-100" />
                    <div className="h-2 w-5/6 rounded bg-black-100" />
                    <div className="h-2 w-2/3 rounded bg-black-100" />
                    <div className="mt-5 h-3 w-1/2 rounded bg-black-200" />
                    <div className="h-2 w-full rounded bg-black-100" />
                    <div className="h-2 w-4/5 rounded bg-black-100" />
                    <div className="mt-5 grid grid-cols-3 gap-2.5">
                      <div className="h-14 rounded-md bg-gradient-to-br from-red-50 to-red-100 ring-1 ring-red-200" />
                      <div className="h-14 rounded-md bg-gradient-to-br from-yellow-50 to-yellow-100 ring-1 ring-yellow-200" />
                      <div className="h-14 rounded-md bg-gradient-to-br from-primary-50 to-primary-100 ring-1 ring-primary-200" />
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="h-2 w-full rounded bg-black-100" />
                      <div className="h-2 w-3/4 rounded bg-black-100" />
                    </div>
                  </div>
                </>
              )}

              {callouts.map((c, i) => (
                <span
                  key={c.id ?? `${c.position}-${i}`}
                  className={clsx(
                    "absolute hidden rounded-full px-3 py-1.5 text-xs font-medium shadow-xl lg:inline-block",
                    CALLOUT_COLORS[i % CALLOUT_COLORS.length],
                    CALLOUT_ROTATIONS[i % CALLOUT_ROTATIONS.length],
                    CALLOUT_POSITION_CLASS[c.position],
                  )}
                >
                  {c.text}
                </span>
              ))}

              <span className="absolute bottom-3 right-3 rounded-full bg-black-950/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                Sample · Redacted
              </span>
            </div>

            {(sampleBriefCaption || secondaryCta?.link) && (
              <div className="mt-6 text-center lg:text-left">
                {sampleBriefCaption && (
                  <p className="font-bricolage-grotesque text-base font-medium text-black-900 md:text-lg">
                    {sampleBriefCaption}
                  </p>
                )}
                {secondaryCta?.link && secondaryCta?.text && (
                  <div className="mt-3 flex justify-center lg:justify-start">
                    <ActionButton
                      id={secondaryCta.id}
                      href={secondaryCta.link}
                      target={secondaryCta.target}
                      mode="link"
                    >
                      {secondaryCta.text}
                    </ActionButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowEngagementRunsSection;
